import { useState, useEffect } from "react";
import "./ClubTransfer.scss";
import { useConfig } from "../ConfigProvider";
import { useTunnelData } from "../TunnelContext";
import Button from "../Button";
import Step from "./Step";
import Api from "../baseApi";
import StyledStepFooter from "../StyledStepFooter";
import { useAnalytics } from "../analytics";

/**
 * Remplace {variable} par les valeurs et ##text## par <strong>text</strong>
 */
function renderTemplate(template, vars) {
  if (!template) return null;
  let text = template;
  Object.entries(vars).forEach(([key, value]) => {
    text = text.replace(new RegExp(`\\{${key}\\}`, "g"), value || "");
  });
  const parts = text.split(/(##[^#]+##)/g);
  return parts.map((part, i) => {
    if (part.startsWith("##") && part.endsWith("##")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function ClubTransferScreen({ onCancel, onComplete }) {
  const config = useConfig();
  const CT = config.clubTransfer;
  const { tunnel } = useTunnelData();
  const { trackClubTransferCompleted } = useAnalytics();

  useEffect(() => {
    console.log("[ClubTransfer] Screen mounted");
    console.log("[ClubTransfer] tunnel.user.email:", tunnel.user?.email);
    console.log("[ClubTransfer] tunnel.club:", tunnel.club?.name, "r2vel:", tunnel.club?.r2vel, "msdsId:", tunnel.club?.msdsId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [subStep, setSubStep] = useState("REQUEST"); // REQUEST, CODE, CONFIRM
  const [contact, setContact] = useState(null);
  const [migrationRequest, setMigrationRequest] = useState(null);
  const [smsCode, setSmsCode] = useState("");
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const clubName = tunnel.club?.name || "";
  const email = tunnel.user?.email || "";

  useEffect(() => {
    const [query] = Api.GET({
      path: `/api/vel/${tunnel.club?.r2vel}/contacts`,
      query: { email, msdsId: tunnel.club?.msdsId, tunnelUid: tunnel.uid },
    });

    query
      .then((data) => {
        console.debug("[ClubTransfer] Contact fetched", data);
        setContact(data);
        setFetchLoading(false);
      })
      .catch((err) => {
        console.error("[ClubTransfer] Failed to fetch contact", err);
        setFetchError(err.message || CT.errorMessage);
        setFetchLoading(false);
      });
  }, [email]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const handleSendCode = () => {
    setSendLoading(true);
    setErrorMsg(null);
    setSmsCode(""); 

    const [query] = Api.POST({
      path: `/api/vel/${tunnel.club?.r2vel}/contacts/migrationRequest`,
      body: {
        contactId: contact?.contact?.contactId,
        msdsId: tunnel.club?.msdsId,
        tunnelUid: tunnel.uid,
      },
    });

    query
      .then((data) => {
        console.log("[ClubTransfer] migrationRequest response:", data);
        setMigrationRequest(data);
        setSubStep("CODE");
        setSendLoading(false);
      })
      .catch((err) => {
        console.error("[ClubTransfer] migrationRequest error:", err);
        setErrorMsg(err.message || CT.errorMessage);
        setSendLoading(false);
      });
  };

  const handleConfirmCode = () => {
    setConfirmLoading(true);
    setErrorMsg(null);

    const [query] = Api.POST({
      path: `/api/vel/${tunnel.club?.r2vel}/contacts/migrationConfirm`,
      body: {
        contactId: contact?.contact?.contactId,
        signatureRequestId: migrationRequest?.['@id'],
        code: smsCode,
        msdsId: tunnel.club?.msdsId,
        tunnelUid: tunnel.uid,
      },
    });

    query
      .then((data) => {
        console.log("[ClubTransfer] confirm response:", data);
        trackClubTransferCompleted();
        setSubStep("CONFIRM");
        setConfirmLoading(false);
      })
      .catch((err) => {
        console.error("[ClubTransfer] confirm error:", err);
        setErrorMsg(err.message || CT.errorMessage);
        setConfirmLoading(false);
      });
  };

  const handleFinalConfirmation = () => {
    console.log("[ClubTransfer] Final confirmation -> back to form");
    onComplete();
  }

  if (fetchLoading || sendLoading || confirmLoading) {
    let loadingText = CT.loadingLabel;
    if (fetchLoading)
      loadingText = config.loadingDefault;
    else if (confirmLoading)
      loadingText = CT.confirmLoadingLabel;
    return (
      <Step classe="ClubTransfer" step="Vos coordonnées" loading={loadingText} />
    );
  }
  
  const maskedPhone = contact?.contact?.mobile;

  switch (subStep) {
    case "REQUEST":
      return (
        <Step classe="ClubTransfer" step="Vos coordonnées" loading={false}>
          <section>
            <h2>
              {CT.requestTitle}
            </h2>

            <p style={{ marginBottom: "0.25rem" }}>
              {renderTemplate(CT.requestMessage, { email, clubName, phone: maskedPhone })}
            </p>
            <p style={{ marginBottom: "0.25rem" }}>
              {renderTemplate(CT.requestMessageWithEmail, { email, clubName, phone: maskedPhone })}
            </p>
            <p style={{ marginBottom: "0.25rem" }}>
              {renderTemplate(maskedPhone ? CT.requestInstructionsWithPhone : CT.requestInstructions, { email, clubName, phone: maskedPhone })}
            </p>
          </section>

          <StyledStepFooter>
            {(!!errorMsg || !!fetchError) && (
              <p style={{ textAlign: "center", fontWeight: "bold", marginBottom: "1rem" }} className="errorMsg">
                {errorMsg || fetchError}
              </p>
            )}

            <Button variant="primary" className="primary" onClick={handleSendCode} disabled={!!fetchError}>
              {CT.requestButtonLabel}
            </Button>
            <Button style={{ marginTop: "0.75rem" }} onClick={onCancel}>
              {CT.requestCancelLabel}
            </Button>
          </StyledStepFooter>
        </Step>
      );

    case "CODE":
      return (
        <Step classe="ClubTransfer" step="Vos coordonnées" loading={false}>
          <section>
            <h2>
              {CT.codeTitle}
            </h2>

            <p style={{ marginBottom: "1.5rem" }}>
              {CT.codeMessage}
            </p>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              value={smsCode}
              // La saisie automatique iOS depuis le SMS peut inclure des espaces
              // (code groupé "12 34" ou espace final) : on ne garde que les chiffres.
              onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ""))}
              placeholder={CT.codePlaceholder}
              className="club-transfer-code-input"
            />
          </section>

          <StyledStepFooter>
            {!!errorMsg && (
              <p style={{ textAlign: "center", fontWeight: "bold", marginBottom: "1rem" }} className="errorMsg">
                {errorMsg}
              </p>
            )}

            <Button variant="primary" className="primary" onClick={handleConfirmCode} disabled={smsCode.length < 4}>
              {CT.codeButtonLabel}
            </Button>
            <Button style={{ marginTop: "0.75rem" }} onClick={handleSendCode}>
              {CT.codeResendButtonLabel}
            </Button>
          </StyledStepFooter>
        </Step>
      );
      
    case "CONFIRM":
      return (
        <Step classe="ClubTransfer" step="Vos coordonnées" loading={false}>
          <section>
            <h2>
              {CT.confirmTitle}
            </h2>

            <p style={{ marginBottom: "1.5rem" }}>
              {CT.confirmMessage}
            </p>
          </section>

          <StyledStepFooter>
            <Button variant="primary" className="primary" onClick={handleFinalConfirmation}>
              {CT.confirmButtonLabel}
            </Button>
          </StyledStepFooter>
        </Step>
      );

    default:
      return null;
  }
}
